import { useState } from "react";
import { BellRing, Plus, RotateCcw, Trash2 } from "lucide-react";

import type { PriceAlarm } from "../api/types";

interface SymbolPriceAlarmsSectionProps {
  symbol: string | null;
  alarms: PriceAlarm[];
  alarmEmailEnabled: boolean;
  /** Latest close, used only to tell the user which way a target will be read. */
  referencePrice: number | null;
  pendingAlarmId: number | null;
  isCreating: boolean;
  onCreate: (targetPrice: number) => void;
  onRearm: (alarmId: number) => void;
  onDelete: (alarmId: number) => void;
}

function formatPrice(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return "--";
  return `$${value.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function formatFiredDate(value: string): string {
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleDateString();
}

export function SymbolPriceAlarmsSection({
  symbol,
  alarms,
  alarmEmailEnabled,
  referencePrice,
  pendingAlarmId,
  isCreating,
  onCreate,
  onRearm,
  onDelete,
}: SymbolPriceAlarmsSectionProps) {
  const [draft, setDraft] = useState("");

  const parsedDraft = Number.parseFloat(draft);
  const draftIsValid = Number.isFinite(parsedDraft) && parsedDraft > 0;

  // The server resolves direction from the price at creation time; previewing
  // the same rule here means the user sees what their number will mean before
  // they commit to it, rather than discovering it in the list afterwards.
  const previewDirection =
    draftIsValid && referencePrice !== null
      ? parsedDraft < referencePrice
        ? "falls to"
        : "rises to"
      : null;

  const submit = () => {
    if (!draftIsValid || !symbol) return;
    onCreate(parsedDraft);
    setDraft("");
  };

  return (
    <div className="agent-signals-section">
      <div className="companion-heading">
        <p className="panel-eyebrow">Price Alarms</p>
      </div>
      <div className="companion-status">
        {symbol
          ? `Email me when ${symbol} reaches a price`
          : "Select a symbol to set a price alarm."}
      </div>

      {!alarmEmailEnabled && (
        <p className="agent-signals-note">
          Email alarms are not configured on this server yet - alarms you add below will be
          saved, but no mail will send until SMTP is set up.
        </p>
      )}

      <p className="agent-signals-note">
        Alarms are checked against the daily close, so one reports the first close past your
        price rather than an intraday touch. Each fires once, then waits to be re-armed.
      </p>

      <div className="price-alarm-form">
        <input
          type="number"
          className="price-alarm-input"
          placeholder={referencePrice !== null ? formatPrice(referencePrice) : "Price"}
          value={draft}
          min="0"
          step="any"
          disabled={!symbol || isCreating}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") submit();
          }}
          aria-label={symbol ? `Price to watch on ${symbol}` : "Price to watch"}
        />
        <button
          type="button"
          className="price-alarm-add"
          disabled={!symbol || !draftIsValid || isCreating}
          onClick={submit}
          title={
            previewDirection && symbol
              ? `Email me when ${symbol} ${previewDirection} ${formatPrice(parsedDraft)}`
              : "Add a price alarm"
          }
        >
          <Plus size={16} />
          Add
        </button>
      </div>

      {previewDirection && symbol && (
        <p className="price-alarm-preview">
          Will alert when {symbol} <strong>{previewDirection}</strong>{" "}
          {formatPrice(parsedDraft)} (now {formatPrice(referencePrice)}).
        </p>
      )}

      <div className="agent-signals-list">
        {alarms.length === 0 && (
          <p className="agent-signal-reason">
            {symbol ? `No price alarms on ${symbol} yet.` : ""}
          </p>
        )}

        {alarms.map((alarm) => {
          const isPending = pendingAlarmId === alarm.id;
          const hasFired = alarm.triggered_at !== null;
          const reached = alarm.direction === "above" ? "rises to" : "falls to";

          return (
            <div className="agent-signal-row" key={alarm.id}>
              <div className="agent-signal-main">
                <BellRing size={14} className={hasFired ? "price-alarm-fired-icon" : undefined} />
                <span className="agent-signal-title">
                  {reached} {formatPrice(alarm.target_price)}
                </span>
              </div>
              <p className="agent-signal-reason">
                {hasFired
                  ? `Fired ${formatFiredDate(alarm.triggered_at as string)} at ${formatPrice(
                      alarm.triggered_price,
                    )}.`
                  : `Watching. Was ${formatPrice(alarm.reference_price)} when set.`}
              </p>
              <div className="price-alarm-actions">
                {hasFired && (
                  <button
                    type="button"
                    className="bell-toggle"
                    disabled={isPending}
                    onClick={() => onRearm(alarm.id)}
                    title={`Watch ${formatPrice(alarm.target_price)} again`}
                  >
                    <RotateCcw size={15} />
                  </button>
                )}
                <button
                  type="button"
                  className="bell-toggle"
                  disabled={isPending}
                  onClick={() => onDelete(alarm.id)}
                  title={`Remove this alarm`}
                >
                  <Trash2 size={15} />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
